import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAstroPaymentLinkHistory,
  getAstrologersForDropdown,
} from "../../services/astroPaymentLinkHistoryService";
import { toast } from "react-toastify";

// Response shape from API:
// {
//   success: true,
//   message: "...",
//   summary: { totalAmount, totalAstroCut, totalAdminCut },
//   data: [ ...records ],
//   total: <number>,       ← may come from backend
//   page: <number>,        ← may come from backend
// }

const initialState = {
  records:     [],
  total:       0,
  page:        1,
  totalPages:  1,
  summary: {
    totalAmount:   0,
    totalAstroCut: 0,
    totalAdminCut: 0,
  },
  status: "idle",
  error:  null,

  // dropdown
  astrologers:       [],
  astrologersStatus: "idle",
};

// ── Thunks ───────────────────────────────────────────────────────────────────

export const fetchAstroPaymentLinkHistory = createAsyncThunk(
  "astroPaymentLinkHistory/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      return await getAstroPaymentLinkHistory(params);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Failed to fetch payment link history";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const fetchAstrologersDropdown = createAsyncThunk(
  "astroPaymentLinkHistory/fetchAstrologers",
  async (_, { rejectWithValue }) => {
    try {
      return await getAstrologersForDropdown();
    } catch (error) {
      return rejectWithValue("Failed to fetch astrologers");
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const astroPaymentLinkHistorySlice = createSlice({
  name: "astroPaymentLinkHistory",
  initialState,
  reducers: {
    clearError:  (state) => { state.error = null; },
    resetState:  ()      => initialState,
  },
  extraReducers: (builder) => {
    builder
      // fetchAstroPaymentLinkHistory
      .addCase(fetchAstroPaymentLinkHistory.pending, (state) => {
        state.status = "loading";
        state.error  = null;
      })
      .addCase(fetchAstroPaymentLinkHistory.fulfilled, (state, action) => {
        const payload    = action.payload;          // raw API response
        state.status     = "succeeded";
        state.records    = payload.data   || [];
        state.summary    = payload.summary || {
          totalAmount: 0, totalAstroCut: 0, totalAdminCut: 0,
        };
        // ✅ API returns { pagination: { total, page, limit, totalPages } }
        state.total      = payload.pagination?.total     || payload.total || 0;
        state.page       = payload.pagination?.page      || payload.page  || 1;
        state.totalPages = payload.pagination?.totalPages || 1;
        state.error      = null;
      })
      .addCase(fetchAstroPaymentLinkHistory.rejected, (state, action) => {
        state.status  = "failed";
        state.error   = action.payload;
        state.records = [];
        state.total   = 0;
        state.page    = 1;
      })

      // fetchAstrologersDropdown
      .addCase(fetchAstrologersDropdown.pending, (state) => {
        state.astrologersStatus = "loading";
      })
      .addCase(fetchAstrologersDropdown.fulfilled, (state, action) => {
        state.astrologersStatus = "succeeded";
        const payload           = action.payload;
        // handle whatever shape the dropdown API returns
        state.astrologers =
          payload.data?.astrologers ||
          payload.data?.data        ||
          payload.data              ||
          [];
      })
      .addCase(fetchAstrologersDropdown.rejected, (state) => {
        state.astrologersStatus = "failed";
        state.astrologers       = [];
      });
  },
});

export const { clearError, resetState } = astroPaymentLinkHistorySlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────────
export const selectPaymentLinkRecords    = (s) => s.astroPaymentLinkHistory.records;
export const selectPaymentLinkTotal      = (s) => s.astroPaymentLinkHistory.total;
export const selectPaymentLinkPage       = (s) => s.astroPaymentLinkHistory.page;
export const selectPaymentLinkSummary    = (s) => s.astroPaymentLinkHistory.summary;
export const selectPaymentLinkStatus     = (s) => s.astroPaymentLinkHistory.status;
export const selectAstrologersDropdown   = (s) => s.astroPaymentLinkHistory.astrologers;

export default astroPaymentLinkHistorySlice.reducer;